/**
 * BI — Auditoria (XLSX). Modal de tema (Entrada/Saída/Estoque) +
 * filtros dinâmicos. Faz POST JSON em /reports/audit e baixa o .xlsx.
 */
import api from '../api.js';
import { translate } from '../../versa/filter.js';
import msgbox, { notify } from '../../versa/msgbox.js';
import dialog from '../../versa/dialog.js';
import progress, { downloadResponseWithProgress } from '../../versa/progress.js';
import { formatBiHttpError } from './bi-http-error.js';

function $(sel, root = document) {
	return root.querySelector(sel);
}

function $all(sel, root = document) {
	return Array.from(root.querySelectorAll(sel));
}

function filterExprCompany(companyId) {
	return translate({
		'field-0': 'company_id',
		'operator-0': 'eq',
		'value-0': companyId,
	});
}

function skuLabel(p) {
	if (!p?.sku) return '';
	return Array.isArray(p.sku) ? p.sku.join('') : String(p.sku);
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	let list;
	try {
		list = await res.json();
	} catch (e) {
		notify('Resposta de empresas inválida.', 'Empresas');
		return;
	}
	if (!Array.isArray(list)) return;
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

async function loadWarehouses(companyId, sel) {
	sel.innerHTML = '<option value="">Todos</option>';
	if (!companyId) return;
	const fe = encodeURIComponent(filterExprCompany(companyId));
	const res = await api.list_warehouses(`?filter=${fe}&limit=0&sort=2`);
	if (!res.ok) return;
	const arr = await res.json();
	if (!Array.isArray(arr)) return;
	for (const w of arr) {
		const o = document.createElement('option');
		o.value = String(w.id);
		o.textContent = `${w.name ?? ''} (${w.id})`;
		sel.appendChild(o);
	}
}

async function loadPrograms(companyId, sel) {
	sel.innerHTML = '<option value="">Todos</option>';
	if (!companyId) return;
	const res = await api.list_programs_by_company(companyId);
	if (!res.ok) return;
	const arr = await res.json();
	if (!Array.isArray(arr)) return;
	for (const p of arr) {
		const o = document.createElement('option');
		o.value = String(p.id);
		o.textContent = p.name || String(p.id);
		sel.appendChild(o);
	}
}

async function loadProductsForCompany(companyId, selectEl, searchInput) {
	selectEl.innerHTML = '';
	if (!companyId) {
		selectEl.innerHTML = '<option value="">— Escolha a empresa —</option>';
		return;
	}
	selectEl.innerHTML = '<option value="">Carregando produtos…</option>';
	selectEl.disabled = true;
	const mb = msgbox({ animation: true, text: 'Carregando produtos…' });
	try {
		const fe = filterExprCompany(companyId);
		const batch = 500;
		const products = [];
		const seen = new Set();
		for (let offset = 0; ; offset += batch) {
			const q = `?filter=${encodeURIComponent(fe)}&limit=${batch}&offset=${offset}&sort=7&include_disabled=1`;
			const res = await api.list_products(q);
			if (!res.ok) {
				notify(await res.text(), 'Produtos');
				selectEl.innerHTML = '<option value="">— Erro —</option>';
				return;
			}
			const chunk = await res.json();
			if (!Array.isArray(chunk) || chunk.length === 0) break;
			for (const p of chunk) {
				const id = p?.id;
				if (id == null || seen.has(id)) continue;
				seen.add(id);
				products.push(p);
			}
			if (chunk.length < batch) break;
		}
		selectEl.innerHTML = '<option value="">— Todos —</option>';
		for (const p of products) {
			const sku = skuLabel(p);
			const name = (p.name || '').trim() || '(sem nome)';
			const o = document.createElement('option');
			o.value = String(p.id);
			const dis = p.is_disabled ? ' [inativo]' : '';
			o.textContent = `${sku} — ${name} (id ${p.id})${dis}`;
			selectEl.appendChild(o);
		}
		if (searchInput) applyProductSearch(selectEl, searchInput.value);
	} finally {
		selectEl.disabled = false;
		mb.destroy();
	}
}

function applyProductSearch(selectEl, needle) {
	const n = (needle || '').trim().toLowerCase();
	if (!n) {
		for (const o of selectEl.options) o.hidden = false;
		return;
	}
	for (const o of selectEl.options) {
		if (!o.value) {
			o.hidden = false;
			continue;
		}
		o.hidden = !String(o.textContent || '').toLowerCase().includes(n);
	}
}

function nowDateTimeLocalDefault(daysAgo) {
	const d = new Date();
	if (daysAgo) d.setDate(d.getDate() - daysAgo);
	const pad = (n) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function readDatetime(input) {
	const v = (input?.value || '').trim();
	return v ? v.replace('T', ' ') + ':00' : '';
}

function buildPayload(node, theme) {
	const company_id = $('#bia-company', node)?.value?.trim();
	if (!company_id) return { error: 'Selecione a empresa.' };
	const date_from = readDatetime($('#bia-date-from', node));
	const date_to = readDatetime($('#bia-date-to', node));
	if (!date_from || !date_to) return { error: 'Informe o período (datas com horário).' };
	const filters = {
		date_from,
		date_to,
		product_id: $('#bia-product', node)?.value || null,
		program_id: $('#bia-program', node)?.value || null,
		lot_id: ($('#bia-lot', node)?.value || '').trim() || null,
	};
	if (theme === 'input') {
		filters.input_id = ($('#bia-input-id', node)?.value || '').trim() || null;
		filters.invoice_number = ($('#bia-invoice-number', node)?.value || '').trim() || null;
		filters.supplier_id = ($('#bia-supplier-id', node)?.value || '').trim() || null;
	} else if (theme === 'output') {
		filters.output_id = ($('#bia-output-id', node)?.value || '').trim() || null;
		filters.output_order_id = ($('#bia-output-order-id', node)?.value || '').trim() || null;
		filters.customer_id = ($('#bia-customer-id', node)?.value || '').trim() || null;
	} else if (theme === 'stock') {
		filters.warehouse_id = $('#bia-warehouse', node)?.value || null;
		filters.location_id = ($('#bia-location-id', node)?.value || '').trim() || null;
	}
	for (const k of Object.keys(filters)) {
		if (filters[k] === '' || filters[k] == null) delete filters[k];
	}
	return { company_id, payload: { theme, filters } };
}

function downloadXlsx(blob, filename) {
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'auditoria.xlsx';
	a.click();
	URL.revokeObjectURL(a.href);
}

function setBtnState(btn, loading, label) {
	if (!btn) return;
	if (loading) {
		btn.dataset.label = btn.textContent;
		btn.textContent = label || 'Gerando…';
		btn.disabled = true;
	} else {
		btn.textContent = btn.dataset.label || 'Gerar Excel';
		btn.disabled = false;
	}
}

async function postAudit(companyId, payload) {
	const res = await api.audit_report(companyId, payload);
	return res;
}

async function finishXlsx(res, dlg, pg) {
	if (!res.ok) {
		const t = await res.text();
		notify(formatBiHttpError(res.status, t), 'Auditoria');
		return;
	}
	pg.markStage('Recebendo arquivo…');
	const blob = await downloadResponseWithProgress(res, pg);
	if (!blob || blob.size === 0) {
		notify('Nenhum dado encontrado para os filtros aplicados.', 'Auditoria');
		return;
	}
	const cd = res.headers.get('Content-Disposition');
	let fname = 'auditoria.xlsx';
	if (cd) {
		const m = cd.match(/filename="?([^";]+)"?/i);
		if (m) fname = m[1];
	}
	downloadXlsx(blob, fname);
	dlg.destroy();
	notify('Download do Excel iniciado.', 'Auditoria');
}

function themeFiltersHtml(theme) {
	if (theme === 'input') {
		return `
			<div class="row">
				<label>Entrada (id Sial)<input type="number" id="bia-input-id" min="1" step="1" placeholder="opcional"></label>
				<label>Nº NF<input type="text" id="bia-invoice-number" placeholder="opcional"></label>
				<label>Fornecedor (id)<input type="number" id="bia-supplier-id" min="1" step="1" placeholder="opcional"></label>
			</div>`;
	}
	if (theme === 'output') {
		return `
			<div class="row">
				<label>Saída (id Sial)<input type="number" id="bia-output-id" min="1" step="1" placeholder="opcional"></label>
				<label>Pedido de saída (id)<input type="number" id="bia-output-order-id" min="1" step="1" placeholder="opcional"></label>
				<label>Cliente (id)<input type="number" id="bia-customer-id" min="1" step="1" placeholder="opcional"></label>
			</div>`;
	}
	return `
		<div class="row">
			<label>Armazém<select id="bia-warehouse"></select></label>
			<label>Posição (id)<input type="number" id="bia-location-id" min="1" step="1" placeholder="opcional"></label>
		</div>`;
}

export default async function bi_audit() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.bia-hint { color: #555; font-size: 0.9rem; max-width: 50rem; }
			.bia-modal label { display: block; margin-top: 0.65rem; }
			.bia-modal .row { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; align-items: flex-end; margin-top: 0.35rem; }
			.bia-modal .row > label { flex: 1 1 14rem; }
			.bia-product-select { width: 100%; min-height: 9rem; margin-top: 0.35rem; font-size: 0.88rem; }
			.bia-actions { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
			.bia-themes { display: flex; gap: 0.75rem; margin-top: 0.5rem; flex-wrap: wrap; }
			.bia-theme-card { flex: 1 1 11rem; min-height: 5.5rem; border: 2px solid #ccc; border-radius: 0.5rem; padding: 0.75rem; cursor: pointer; background: #fff; }
			.bia-theme-card.selected { border-color: #1F4E78; background: #E8F1FB; }
			.bia-theme-card .ti { font-size: 1.2rem; font-weight: bold; }
			.bia-theme-card .desc { color: #555; font-size: 0.85rem; margin-top: 0.25rem; }
			#bia-theme-fields { margin-top: 0.5rem; }
		</style>
		<div class="title">Auditoria (BI)</div>
		<p class="mts bia-hint">Gera um arquivo <strong>Excel (.xlsx)</strong> com 4 abas (Resumo, Histórico de Status, Movimentos de Estoque, Participantes) para o tema escolhido. Histórico de status é derivado das transações de estoque. Limite padrão: 10 mil linhas por aba.</p>
		<p class="mts"><button type="button" class="button" id="bia-open-modal">Abrir filtros</button></p>
	`;

	const openModal = () => {
		const dlgHtml = `
<div class="msgbox bia-modal" style="min-width: min(46rem, 94vw); max-width: 96vw; text-align: left;">
	<div class="mb-title">Auditoria — filtros</div>
	<div class="mb-text" style="max-height: none;">
		<label>Tema do relatório<sup>*</sup></label>
		<div class="bia-themes" id="bia-theme-cards">
			<div class="bia-theme-card selected" data-theme="input">
				<div class="ti">Entrada</div>
				<div class="desc">Auditoria de uma entrada (recebimento, lotes vinculados, NF).</div>
			</div>
			<div class="bia-theme-card" data-theme="output">
				<div class="ti">Saída</div>
				<div class="desc">Auditoria de uma saída/pedido (separação, expedição, cancelamentos).</div>
			</div>
			<div class="bia-theme-card" data-theme="stock">
				<div class="ti">Estoque</div>
				<div class="desc">Auditoria por filtro composto (produto/lote/posição) num período.</div>
			</div>
		</div>
		<label>Empresa<sup>*</sup><select id="bia-company"></select></label>
		<div class="row">
			<label>Período — início<sup>*</sup><input type="datetime-local" id="bia-date-from"></label>
			<label>Período — fim<sup>*</sup><input type="datetime-local" id="bia-date-to"></label>
		</div>
		<div class="row">
			<label>Programa<select id="bia-program"></select></label>
			<label>Lote (id)<input type="number" id="bia-lot" min="1" step="1" placeholder="opcional"></label>
		</div>
		<label>Filtrar lista de produtos (SKU ou nome)<input type="search" id="bia-prod-filter" placeholder="opcional" autocomplete="off"></label>
		<label>Produto<select id="bia-product" class="bia-product-select" size="8"></select></label>
		<div id="bia-theme-fields"></div>
	</div>
	<div class="bia-actions">
		<button type="button" class="button bn_Generate">Gerar Excel</button>
		<button type="button" class="button bn_Escape">Fechar</button>
	</div>
</div>`;

		const dlg = dialog.create(dlgHtml, async (key) => {
			const root = dlg.getNode();
			if (key === 'Escape') {
				dlg.destroy();
				return;
			}
			if (key !== 'Generate') return;
			const theme = root.querySelector('.bia-theme-card.selected')?.dataset?.theme || 'input';
			const built = buildPayload(root, theme);
			if (built.error) {
				notify(built.error, 'Auditoria');
				return;
			}
			const btn = root.querySelector('.bn_Generate');
			setBtnState(btn, true, 'Gerando…');
			const pg = progress({
				title: 'Gerando auditoria (XLSX)…',
				text: 'O servidor está consultando transações, status e participantes. Pode levar alguns instantes para conjuntos grandes.',
				indeterminate: true,
			});
			try {
				pg.markStage('Aguardando resposta do servidor…');
				let res = await postAudit(built.company_id, built.payload);
				if (res.status === 409) {
					let info = {};
					try { info = await res.json(); } catch (e) { /* ignore */ }
					if (info?.exceeded) {
						pg.destroy();
						const ok = window.confirm(
							`A aba «${info.sheet}» tem ${info.count} linhas (acima do limite recomendado de 10.000). ` +
							`O ficheiro será truncado nessa aba. Deseja continuar mesmo assim?`
						);
						if (!ok) {
							setBtnState(btn, false);
							return;
						}
						const pg2 = progress({
							title: 'Gerando auditoria (XLSX) — modo estendido…',
							text: 'Confirmado: gerando arquivo com mais de 10 mil linhas em alguma aba.',
							indeterminate: true,
						});
						pg2.markStage('Aguardando resposta do servidor…');
						try {
							res = await postAudit(built.company_id, {
								...built.payload,
								confirm_over_limit: true,
							});
							await finishXlsx(res, dlg, pg2);
						} finally {
							pg2.destroy();
							setBtnState(btn, false);
						}
						return;
					}
				}
				await finishXlsx(res, dlg, pg);
			} catch (e) {
				console.error(e);
				notify(e?.message || String(e), 'Auditoria');
			} finally {
				pg.destroy();
				setBtnState(btn, false);
			}
		});

		const root = dlg.getNode();
		const selCo = $('#bia-company', root);
		const selPr = $('#bia-program', root);
		const selProd = $('#bia-product', root);
		const inpFilt = $('#bia-prod-filter', root);
		const dtFrom = $('#bia-date-from', root);
		const dtTo = $('#bia-date-to', root);
		const themeFields = $('#bia-theme-fields', root);
		const themeCards = $all('.bia-theme-card', root);

		dtFrom.value = nowDateTimeLocalDefault(7);
		dtTo.value = nowDateTimeLocalDefault(0);

		const renderThemeFields = (theme) => {
			themeFields.innerHTML = themeFiltersHtml(theme);
			if (theme === 'stock') {
				const wh = $('#bia-warehouse', themeFields);
				if (wh) loadWarehouses(selCo.value, wh);
			}
		};
		renderThemeFields('input');

		themeCards.forEach((card) => {
			card.addEventListener('click', () => {
				themeCards.forEach((c) => c.classList.remove('selected'));
				card.classList.add('selected');
				renderThemeFields(card.dataset.theme);
			});
		});

		loadCompanies(selCo).then(() => {});
		selCo.addEventListener('change', async () => {
			const id = selCo.value;
			await Promise.all([
				loadPrograms(id, selPr),
				loadProductsForCompany(id, selProd, inpFilt),
			]);
			const wh = $('#bia-warehouse', themeFields);
			if (wh) await loadWarehouses(id, wh);
		});
		inpFilt.addEventListener('input', () => applyProductSearch(selProd, inpFilt.value));
	};

	$('#bia-open-modal', work).addEventListener('click', openModal);
}
